/**
 * The PURE view of a re-opened saved question (`SavedRunPanel` wires the hook to it).
 * Its own module ON PURPOSE: it imports no network layer (`@/providers/admin-api`
 * reads `import.meta.env` when it loads), so every state renders in
 * frontend/admin/render-tests with no network and no Vite environment.
 */
import { MessageSquare, RefreshCw, X } from 'lucide-react';

import type { SavedRunBody } from '../protocol';
import { AnswerFigures } from './AnswerFigures';

export interface SavedRunState {
  run: SavedRunBody | null;
  loading: boolean;
  notFound: boolean;
  error: string | null;
}

export function SavedRunView({
  label,
  state,
  onRerun,
  onAsk,
  onClose,
}: {
  label: string;
  state: SavedRunState;
  onRerun: () => void;
  onAsk: () => void;
  onClose: () => void;
}) {
  const { run, loading, notFound, error } = state;
  return (
    <section
      aria-label={`Saved question: ${label}`}
      className="mx-auto mb-4 w-full max-w-3xl space-y-3 rounded-lg border bg-card p-4"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Saved question</p>
          <h3 className="truncate text-sm font-semibold">{label}</h3>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={onRerun}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw className="h-3 w-3" aria-hidden />
            Re-run
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close the saved question"
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </header>

      {loading ? (
        <div className="space-y-2" aria-busy="true">
          <div className="h-4 w-1/3 motion-safe:animate-pulse rounded bg-muted" />
          <div className="h-24 w-full motion-safe:animate-pulse rounded bg-muted" />
        </div>
      ) : notFound ? (
        <p className="text-sm text-muted-foreground">
          This saved question is no longer available: it was deleted, or it is not shared with you.
        </p>
      ) : error !== null || run === null ? (
        <p className="text-sm text-destructive" role="alert">
          Could not run the saved question{error ? `: ${error}` : '.'}
        </p>
      ) : (
        <>
          <p className="rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground" role="note">
            {run.window
              ? `Today's run of the saved question, for ${run.window.from} to ${run.window.to}. `
              : "Today's run of the saved question. "}
            The figures come from the saved metric and its parameters, with no AI summary.
          </p>
          <AnswerFigures answer={run} />
        </>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onAsk}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors hover:bg-muted"
        >
          <MessageSquare className="h-3 w-3" aria-hidden />
          Ask the agent about this
        </button>
      </div>
    </section>
  );
}
